// Fuente única de verdad para el gating de MODOS por plan. Antes esto estaba
// disperso en `plan === 'gratis'` sueltos por InterfazV2; centralizarlo acá
// hace fácil auditar/cambiar qué modo necesita qué plan.
//
// Planes (company_profiles.plan): gratis · emprendimiento (Básico) ·
// negocio (Pro) · enterprise/enterprise1/2/3 (Ultra) · enterprise_plus (Empresa).

const PRO_OR_ULTRA = new Set([
  'negocio',
  'enterprise',
  'enterprise1',
  'enterprise2',
  'enterprise3',
  'enterprise_plus',
]);

// Solo planes Ultra (enterprise + niveles). El custom Empresa (enterprise_plus)
// se incluye porque comparte todas las capabilities de los Ultra.
const ULTRA = new Set([
  'enterprise',
  'enterprise1',
  'enterprise2',
  'enterprise3',
  'enterprise_plus',
]);

const norm = (plan?: string | null) => (plan ?? '').toString().trim().toLowerCase();

/** Pro y Ultra (negocio + enterprise*). Empresa (enterprise_plus) incluido. */
export function isProOrUltra(plan?: string | null): boolean {
  return PRO_OR_ULTRA.has(norm(plan));
}

/** Solo planes Ultra (enterprise/enterprise1/2/3 + el custom Empresa
 *  enterprise_plus). Excluye Pro (negocio). */
export function isUltra(plan?: string | null): boolean {
  return ULTRA.has(norm(plan));
}

/** Cualquier plan pago (distinto de gratis). */
export function isPaidPlan(plan?: string | null): boolean {
  const p = norm(plan);
  return p.length > 0 && p !== 'gratis';
}

export interface ModePlanLock {
  /** Identificador de la feature bloqueada (para telemetría / debugging). */
  feature:
    // Acá hubo un `'catalogo'` que cubría los dos catálogos e Infografía, y después un
    // `'infografia'` cuando los catálogos se abrieron. Los dos salieron: ese `if` ya no
    // existe — ADR-2026-08-20-infografia-tambien-se-abre-a-la-prueba-gratis.
    //
    // Con eso, NINGUNA feature de este union pide "plan pago" a secas: las que quedan
    // son todas de Pro/Ultra para arriba, y `isPaidPlan` dejó de usarse en
    // `getModePlanLock` (sigue exportada porque la usan gestionPlan, EnterpriseCheckout
    // y las "Creaciones recientes" de la home).
    | 'asistente'
    | 'editor-video'
    | 'video-influencer'
    | 'animar-imagen';
  title: string;
  message: string;
}

/** Nota corta del plan que requiere una feature bloqueada — la muestra el
 *  visor de ejemplos de modos (ModeExamplesModal) junto al CTA "Mejorar
 *  plan". Espeja las reglas de getModePlanLock. */
export function getPlanRequirementNote(
  feature: ModePlanLock['feature'],
): string {
  switch (feature) {
    // La rama "desde el plan Básico" se fue con el `if` de catálogo/infografía
    // (ADR-2026-08-20-infografia-tambien-se-abre-a-la-prueba-gratis): hoy no queda
    // ninguna feature con ese piso.
    case 'asistente':
    case 'video-influencer':
    case 'animar-imagen':
      return 'Función disponible para los planes Pro y Ultra.';
    case 'editor-video':
      return 'Función disponible únicamente con el plan Ultra.';
  }
}

/**
 * Devuelve el lock a mostrar si el `plan` NO alcanza para el modo `kind`, o
 * `null` si está permitido.
 *
 * Reglas:
 *   • catalogo-ia / catalogo-muebles / infografia → SIN piso de plan
 *     (ADR-2026-08-20-el-catalogo-se-abre-a-la-prueba-gratis + ...-infografia-tambien-...).
 *   • asistente        → SIN piso de plan; el candado bajó a sus dos opciones caras.
 *   • editor-video     → requiere Ultra (el Estudio de Video pro; bloqueado en el resto).
 *   • video-influencer → requiere Pro o Ultra (ADR-564).
 *   • animar-imagen    → requiere Pro o Ultra.
 *
 * IMPORTANTE: si `plan` todavía no se conoce (null/''), NO bloqueamos — así no
 * le negamos el acceso a un usuario pago mientras su plan carga. El guard de
 * generación (que pide el plan fresco a Supabase) queda como backstop.
 */
export function getModePlanLock(
  kind: string | null | undefined,
  plan: string | null | undefined,
): ModePlanLock | null {
  if (!kind || !norm(plan)) return null;

  // LOS TRES MODOS DE LA FAMILIA CATÁLOGO YA NO SE BLOQUEAN: `catalogo-ia`,
  // `catalogo-muebles` e `infografia`.
  //
  // Acá vivía un `if` con `isPaidPlan` para los tres. Se desarmó en dos pasos:
  // ADR-2026-08-20-el-catalogo-se-abre-a-la-prueba-gratis sacó los dos catálogos y dejó
  // Infografía con `if` propio; ADR-2026-08-20-infografia-tambien-se-abre-a-la-prueba-gratis
  // (que lo supersede) sacó también Infografía. Quedan sin piso de plan los tres.
  //
  // Lo único que los limita en la prueba gratis son los CRÉDITOS, y ese corte ya
  // existía: `checkGenerationGuards('catalogo', cost)` chequea saldo SUFICIENTE contra
  // el costo real de la tanda —1 crédito por imagen = vista × producto, ADR-518—, así
  // que una tanda más cara que el saldo no pasa igual.
  //
  // NO dejar un `if` vacío ni un comentario "acá iría el gate": el orden de los `if` de
  // esta función es su semántica, y una rama muerta se lee como una regla viva.

  // "Crear desde cero" (`asistente`) YA NO SE BLOQUEA —
  // ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones.
  //
  // Acá vivía `kind === 'asistente' && !isProOrUltra(plan)`. El modo pasó a estar
  // abierto para todos los planes, y lo que quedó gateado son sus dos opciones caras:
  // la ayuda de IA y la salida en video. Ver `puedeUsarAyudaIA` y
  // `puedeGenerarVideoEnAsistente` más abajo.
  //
  // La `feature: 'asistente'` NO se borra del union: la siguen usando
  // `getPlanRequirementNote` y los dos modales de las opciones.

  if (kind === 'video-influencer' && !isProOrUltra(plan)) {
    return {
      feature: 'video-influencer',
      title: 'Video con influencer es una función Pro',
      message:
        'La generación de videos con influencer IA está disponible para los planes Pro y Ultra. Mejorá tu plan para usarla.',
    };
  }

  // Animar imagen consume SEGUNDOS de video: mismo piso de plan que el resto de
  // los modos de video. El botón viejo del historial ya pedía Pro/Ultra, así que
  // esto no restringe nada nuevo — sólo lo mueve a la entrada del modo.
  if (kind === 'animar-imagen' && !isProOrUltra(plan)) {
    return {
      feature: 'animar-imagen',
      title: 'Animar imagen es una función Pro',
      message:
        'Convertir tus imágenes en video está disponible para los planes Pro y Ultra. Mejorá tu plan para usarla.',
    };
  }

  if (kind === 'editor-video' && !isUltra(plan)) {
    return {
      feature: 'editor-video',
      title: 'El Estudio de Video es una función Ultra',
      message:
        'El Estudio de Video con IA está disponible únicamente para el plan Ultra. Mejorá tu plan para desbloquearlo.',
    };
  }

  return null;
}

/* ── "Crear desde cero": el modo se abre, las opciones caras no ──────────────
   ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones.

   El modo entero pedía Pro/Ultra. Ahora entra cualquiera —incluida la prueba
   gratis— y el piso de plan se movió a las dos opciones que cuestan de verdad.

   Las DOS piden `isProOrUltra` y no `isPaidPlan`: es exactamente el piso que el modo
   tenía antes, así que a Básico no le cambia nada. Con `isPaidPlan` habrían quedado
   desbloqueadas para Básico, que es un plan al que hoy no le corresponden — un upgrade
   silencioso colado en un cambio que era de otra cosa.

   OJO: son helpers de UI. El backend NO los conoce, y el camino de generación del
   asistente no consulta el plan (`checkGenerationGuards('agente')` existe pero ningún
   call site lo usa). Por eso además del candado visual hay un corte en el submit. */

/** ¿Puede prender la "ayuda de IA" del asistente? Pro y Ultra. */
export function puedeUsarAyudaIA(plan?: string | null): boolean {
  return isProOrUltra(plan);
}

/** ¿Puede pedir la salida en VIDEO desde el asistente? Pro y Ultra. */
export function puedeGenerarVideoEnAsistente(plan?: string | null): boolean {
  return isProOrUltra(plan);
}

/**
 * Igual que `getModePlanLock` pero para las dos opciones de arriba: devuelve el lock a
 * mostrar, o `null` si está permitida.
 *
 * Mismo criterio de plan desconocido que `getModePlanLock`: con `plan` en null/'' NO se
 * bloquea, así no se le niega la opción a alguien pago mientras su plan carga.
 */
export function getOpcionAsistenteLock(
  opcion: 'ayuda-ia' | 'video',
  plan: string | null | undefined,
): ModePlanLock | null {
  if (!norm(plan)) return null;
  if (opcion === 'ayuda-ia') {
    if (puedeUsarAyudaIA(plan)) return null;
    return {
      feature: 'asistente',
      title: 'La ayuda de IA es una función Pro',
      message:
        'Que Atlas te ayude a armar el pedido está disponible para los planes Pro y Ultra. Podés seguir usando "Crear desde cero" escribiendo vos el pedido.',
    };
  }
  if (puedeGenerarVideoEnAsistente(plan)) return null;
  return {
    feature: 'asistente',
    title: 'Generar video es una función Pro',
    message:
      'La salida en video está disponible para los planes Pro y Ultra. Podés seguir generando imágenes con "Crear desde cero".',
  };
}
